/** @jsxImportSource @emotion/react */
import { SimpleMarkdownRenderer } from './SimpleMarkdownRenderer'
import { SuitabilityBadge } from '@app/domains/suitability/components/SuitabilityBadge'
import { HazardLayerMetadata } from '@app/types/layerTypes'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import CloseIcon from '@mui/icons-material/Close'
import GridViewOutlinedIcon from '@mui/icons-material/GridViewOutlined'
import { Alert, Box, Chip, IconButton, Link, Snackbar, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import type { Theme } from '@mui/material/styles'
import * as React from 'react'
import { Link as RouterLink } from 'react-router-dom'
import {
  dropPlaceholderStyles,
  dropPlaceholderIconStyles,
  dropPlaceholderTitleStyles,
  dropPlaceholderDescriptionStyles,
  columnStyles,
  columnHeaderStyles,
  columnHeaderTextStyles,
  columnTitleStyles,
  columnTitleLinkStyles,
  columnSubtitleStyles,
  closeButtonStyles,
  sectionBlockStyles,
  sectionHeaderStyles,
  sectionContentStyles,
  sectionHeadingStyles,
  fieldLabelStyles,
  mutedValueStyles,
  markdownCellStyles,
  stackedBlocksStyles,
  infoBlockStyles,
  metaLabelStyles,
  noteBadgeStyles,
  ownerBlockStyles,
  ownerNameStyles,
  ownerOrgStyles,
  ownerEmailStyles,
  publicLinkBadgeStyles,
  panelRootStyles,
  useCasesStyles,
  useCaseChipStyles,
  columnsGridStyles,
  emptyStateStyles,
  emptyStateTextStyles,
} from './DataLayerCompareHazardPanel.styles'
import { UseCase } from '@app/domains/hazards/types/hazard.types'

type ComparePanelProps = {
  pinnedLayers: HazardLayerMetadata[]
  useCases: UseCase[]
  activeSlug: string | null
  onChangeSlug: (slug: string) => void
  onDrop: (layer: HazardLayerMetadata) => void
  onUnpin: (layerId: string) => void
  maxReached: boolean
  onDismissMax: () => void
}

const MAX_PINNED = 4

const NullValue = ({ text }: { text?: string }) => (
  <Typography css={mutedValueStyles}>{text ?? 'None noted'}</Typography>
)

type DropPlaceholderProps = {
  onDragOver: (e: React.DragEvent) => void
  onDragLeave: () => void
  onDrop: (e: React.DragEvent) => void
  isDragOver: boolean
  theme: Theme
}

const DropPlaceholder = ({
  onDragOver,
  onDragLeave,
  onDrop,
  isDragOver,
  theme,
}: DropPlaceholderProps) => (
  <Box
    onDragOver={onDragOver}
    onDragLeave={onDragLeave}
    onDrop={onDrop}
    css={dropPlaceholderStyles(theme, isDragOver)}
    data-testid="compare-panel-drop-placeholder"
  >
    <GridViewOutlinedIcon css={dropPlaceholderIconStyles(theme, isDragOver)} />

    <Typography css={dropPlaceholderTitleStyles(theme)}>Pin 2–4 layers to compare</Typography>

    <Typography css={dropPlaceholderDescriptionStyles(theme)}>
      Limitations and fit
      <br />
      for your planning context appear
      <br />
      here
    </Typography>
  </Box>
)

type PinnedColumnProps = {
  layer: HazardLayerMetadata
  activeSlug: string | null
  onUnpin: (id: string) => void
}

const DataLayerCompareHazardPinnedColumn = ({ layer, activeSlug, onUnpin }: PinnedColumnProps) => {
  const meta = layer.metadata
  const activeSuitability = activeSlug
    ? (layer.suitability.find((s) => s.use_case_slug === activeSlug) ?? null)
    : null

  const publicSource = meta?.source_links?.[0] ?? null
  return (
    <Box css={columnStyles} data-testid={`compare-panel-pinned-column-${layer.id}`}>
      <Box css={columnHeaderStyles}>
        <Box css={columnHeaderTextStyles}>
          <Link
            component={RouterLink}
            to={`/app/home/data/detail/${layer.id}/overview`}
            underline="none"
            color="inherit"
            css={columnTitleLinkStyles}
            title={meta?.title ?? layer.id}
          >
            <Typography component="span" css={columnTitleStyles}>
              {meta?.title ?? layer.id}
            </Typography>
          </Link>
          <Typography css={columnSubtitleStyles}>Hazard</Typography>
        </Box>

        <IconButton
          size="small"
          onClick={() => onUnpin(layer.id)}
          css={closeButtonStyles}
          aria-label={`Remove ${meta?.title ?? layer.id} from comparison`}
        >
          <CloseIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Box>

      <Box css={sectionBlockStyles}>
        <Box css={sectionHeaderStyles}>
          <Typography css={sectionHeadingStyles}>Context fit</Typography>
        </Box>
        <Box css={sectionContentStyles} data-align-section="context-fit">
          {activeSuitability ? (
            <SuitabilityBadge letterGrade={activeSuitability.suitability_grade} size="md" />
          ) : (
            <NullValue text="No suitability information available for this layer." />
          )}
        </Box>
      </Box>

      <Box css={sectionBlockStyles}>
        <Box css={sectionHeaderStyles}>
          <Typography css={fieldLabelStyles}>Guidance</Typography>
        </Box>
        <Box css={sectionContentStyles} data-align-section="guidance">
          {activeSuitability?.suitability_statement_md ? (
            <Box css={markdownCellStyles}>
              <SimpleMarkdownRenderer markdown={activeSuitability.suitability_statement_md} />
            </Box>
          ) : (
            <Typography css={mutedValueStyles}>No guidance authored yet.</Typography>
          )}
        </Box>
      </Box>

      <Box css={sectionBlockStyles}>
        <Box css={sectionHeaderStyles}>
          <Typography css={sectionHeadingStyles}>Scenario coverage</Typography>
        </Box>
        <Box css={sectionContentStyles} data-align-section="scenario-coverage">
          <Typography css={mutedValueStyles}>Not available</Typography>
        </Box>
      </Box>

      <Box css={sectionBlockStyles}>
        <Box css={sectionHeaderStyles}>
          <Typography css={sectionHeadingStyles}>Data quality</Typography>
        </Box>
        <Box css={sectionContentStyles} data-align-section="data-quality">
          <Box css={stackedBlocksStyles}>
            <Box>
              <Typography css={fieldLabelStyles}>Limitations</Typography>
              {meta?.limitations_caveats_md ? (
                <Box css={noteBadgeStyles('warning')}>
                  <SimpleMarkdownRenderer markdown={meta.limitations_caveats_md} />
                </Box>
              ) : (
                <Box css={noteBadgeStyles('success')}>None noted</Box>
              )}
            </Box>

            <Box>
              <Typography css={fieldLabelStyles}>Important note</Typography>
              {meta?.additional_notes_md ? (
                <Box css={noteBadgeStyles('warning')}>{meta.additional_notes_md}</Box>
              ) : (
                <NullValue />
              )}
            </Box>

            <Box>
              <Typography css={fieldLabelStyles}>Exclusions / assumptions</Typography>
              {meta?.description_md ? (
                <Box css={noteBadgeStyles('neutral')}>
                  <SimpleMarkdownRenderer markdown={meta.description_md} />
                </Box>
              ) : (
                <NullValue />
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      <Box css={sectionBlockStyles}>
        <Box css={sectionHeaderStyles}>
          <Typography css={sectionHeadingStyles}>Ownership & access</Typography>
        </Box>
        <Box css={sectionContentStyles} data-align-section="ownership-access">
          <Box css={stackedBlocksStyles}>
            <Box css={infoBlockStyles}>
              <Typography css={metaLabelStyles}>Owner</Typography>
              {meta?.owner_name ? (
                <Box css={ownerBlockStyles}>
                  <Typography css={ownerNameStyles}>{meta.owner_name}</Typography>
                  {meta?.owner_organization && (
                    <Typography css={ownerOrgStyles}>{meta.owner_organization}</Typography>
                  )}
                  {meta?.owner_email && (
                    <Link
                      href={`mailto:${meta.owner_email}`}
                      underline="hover"
                      css={ownerEmailStyles}
                    >
                      {meta.owner_email}
                    </Link>
                  )}
                </Box>
              ) : (
                <NullValue />
              )}
            </Box>

            <Box css={infoBlockStyles}>
              <Typography css={metaLabelStyles}>Public data</Typography>
              {publicSource ? (
                <Link
                  href={publicSource.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="none"
                  css={publicLinkBadgeStyles}
                >
                  {publicSource.title || 'Public – online viewer'}
                  <OpenInNewIcon sx={{ fontSize: 11 }} />
                </Link>
              ) : (
                <Typography css={mutedValueStyles}>Not available</Typography>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

export const DataLayerCompareHazardPanel = ({
  pinnedLayers,
  useCases,
  activeSlug,
  onChangeSlug,
  onDrop,
  onUnpin,
  maxReached,
  onDismissMax,
}: ComparePanelProps) => {
  const theme = useTheme()
  const [isDragOver, setIsDragOver] = React.useState(false)

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(true)
  }

  const handleDragLeave = () => setIsDragOver(false)

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
    onDrop(e as unknown as HazardLayerMetadata)
  }

  const showPlaceholder = pinnedLayers.length < 2

  return (
    <Box data-testid="compare-panel" css={panelRootStyles}>
      {useCases.length > 0 && (
        <Box data-testid="compare-panel-use-cases" css={useCasesStyles}>
          {useCases.map((uc) => (
            <Chip
              key={uc.slug}
              data-testid={`compare-panel-use-case-${uc.slug}`}
              label={uc.name}
              size="small"
              variant={activeSlug === uc.slug ? 'filled' : 'outlined'}
              color={activeSlug === uc.slug ? 'primary' : 'default'}
              onClick={() => onChangeSlug(uc.slug)}
              css={useCaseChipStyles}
            />
          ))}
        </Box>
      )}

      <Box
        data-testid="compare-panel-columns"
        css={columnsGridStyles(theme, pinnedLayers.length, showPlaceholder)}
      >
        {pinnedLayers.map((layer) => (
          <DataLayerCompareHazardPinnedColumn
            key={layer.id}
            layer={layer}
            activeSlug={activeSlug}
            onUnpin={onUnpin}
          />
        ))}

        {showPlaceholder && (
          <DropPlaceholder
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            isDragOver={isDragOver}
            theme={theme}
          />
        )}

        {pinnedLayers.length === 0 && !showPlaceholder && (
          <Box css={emptyStateStyles}>
            <Typography variant="body2" css={emptyStateTextStyles(theme)}>
              Pin 2–4 layers to compare
            </Typography>
          </Box>
        )}
      </Box>

      <Snackbar
        open={maxReached}
        autoHideDuration={3000}
        onClose={onDismissMax}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="warning" onClose={onDismissMax} variant="filled">
          Maximum of {MAX_PINNED} layers reached
        </Alert>
      </Snackbar>
    </Box>
  )
}
